import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Layers } from 'lucide-react';

const TESTS = [
{
  key: 'time_dependency',
  name: 'Time Dependency',
  question: 'Does this issue get harder to solve the longer we wait?',
  rockIndicator: 'Delay compounds the problem or closes windows of opportunity'
},
{
  key: 'capability_gap',
  name: 'Capability Gap',
  question: 'Can this be solved with what we already have?',
  rockIndicator: 'Requires capabilities we do not currently possess'
},
{
  key: 'structural_depth',
  name: 'Structural Depth',
  question: 'Is this a symptom or a root cause?',
  rockIndicator: 'Root cause embedded in structure, process, or architecture'
},
{
  key: 'reversibility',
  name: 'Reversibility',
  question: 'If we ignore this, can we easily come back to it?',
  rockIndicator: 'Ignoring creates irreversible or very costly consequences'
},
{
  key: 'dependency_load',
  name: 'Dependency Load',
  question: 'Are other initiatives or issues dependent on resolving this?',
  rockIndicator: 'Multiple downstream items are blocked or degraded'
},
{
  key: 'external_force',
  name: 'External Force',
  question: 'Is this driven by forces outside our control (market, regulation, competitor)?',
  rockIndicator: 'External forcing function with a deadline'
}];


export default function DensityDetectionPanel({ issue, onComplete }) {
  const queryClient = useQueryClient();

  const savedTests = issue?.gate2_tests || {};
  const [tests, setTests] = useState({
    time_dependency: savedTests.time_dependency || false,
    capability_gap: savedTests.capability_gap || false,
    structural_depth: savedTests.structural_depth || false,
    reversibility: savedTests.reversibility || false,
    dependency_load: savedTests.dependency_load || false,
    external_force: savedTests.external_force || false
  });
  const [dissentNotes, setDissentNotes] = useState(issue?.dissent_notes || '');

  const score = Object.values(tests).filter(Boolean).length;

  const getScoreColor = () => {
    if (score <= 1) return 'text-sky-400';
    if (score === 2) return 'text-amber-400';
    return 'text-stone-300';
  };

  const getScoreBgColor = () => {
    if (score <= 1) return 'bg-sky-500/10 border-sky-500/30';
    if (score === 2) return 'bg-amber-500/10 border-amber-500/30';
    return 'bg-stone-600/20 border-stone-500/30';
  };

  const getScoreLabel = () => {
    if (score <= 1) return 'Likely Balloon 🎈';
    if (score === 2) return 'Borderline';
    return 'Likely Rock 🪨';
  };

  const getRecommendation = () => {
    if (score <= 1) return 'Recommendation: Classify as Balloon — route to operational adjustment';
    if (score === 2) return 'Recommendation: Borderline — discuss before classifying';
    return 'Recommendation: Classify as Rock — activate 3-track resolution';
  };

  const classifyMutation = useMutation({
    mutationFn: async ({ classification }) => {
      const user = await base44.auth.me();
      const now = new Date().toISOString();
      const data = {
        classification_gate2: classification,
        gate2_score: score,
        gate2_tests: tests,
        gate2_classified_at: now,
        classified_by: user.email,
        classified_at: now,
        dissent_notes: dissentNotes || issue?.dissent_notes || null
      };
      return base44.entities.Issue.update(issue.id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['issues'] });
      onComplete?.();
    }
  });

  return (
    <div className="space-y-4">
      <div className="text-center mb-2">
        <h3 className="text-slate-600 text-lg font-bold flex items-center justify-center gap-2">Density Detection Tests


        </h3>
        <p className="text-xs text-slate-500 mt-1">Evaluate each diagnostic to determine Balloon vs. Rock density</p>
      </div>

      {/* Test Rows */}
      <div className="space-y-1">
        {TESTS.map((test) =>
        <div key={test.key} className="bg-zinc-50 p-3 rounded-lg border border-slate-700/50">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <p className="text-slate-600 text-sm font-semibold">{test.name}</p>
                <p className="text-xs text-slate-400 mt-0.5">{test.question}</p>
                {tests[test.key] &&
              <p className="text-xs text-amber-400 mt-1 flex items-center gap-1">
                    🪨 {test.rockIndicator}
                  </p>
              }
              </div>
              <Switch
              checked={tests[test.key]}
              onCheckedChange={(checked) => setTests((prev) => ({ ...prev, [test.key]: checked }))} />

            </div>
          </div>
        )}
      </div>

      {/* Score */}
      <div className={`rounded-lg border p-4 text-center ${getScoreBgColor()}`}>
        <p className="text-stone-600 text-2xl font-bold">
          Density Score: {score} / 6
        </p>
        <p className="text-stone-600 mt-0.5 text-sm">{getScoreLabel()}</p>
        <p className="text-stone-600 mt-1 text-xs opacity-80">
          {getRecommendation()}
        </p>
      </div>

      {/* Dissent Notes */}
      <div className="space-y-1">
        <label className="text-xs font-medium text-slate-400">Dissent Notes (if split decision)</label>
        <Textarea data-testid="issues-densitydetectionpanel-textarea"
          value={dissentNotes}
          onChange={(e) => setDissentNotes(e.target.value)} className="bg-gray-50 text-slate-700 px-3 py-2 text-sm rounded-md flex min-h-[60px] w-full border shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm border-slate-700 placeholder:text-slate-600"

          rows={2}
          placeholder="Record any dissenting views from the council..." />

      </div>

      {/* Action Buttons */}
      <div className="flex gap-3">
        <Button
          variant="outline"
          className="flex-1 border-sky-500/40 text-sky-400 hover:bg-sky-500/10"
          onClick={() => classifyMutation.mutate({ classification: 'balloon' })}
          disabled={classifyMutation.isPending}>

          Classify as Balloon 🎈
        </Button>
        <Button
          className="flex-1 bg-stone-600 hover:bg-stone-700 text-white"
          onClick={() => classifyMutation.mutate({ classification: 'rock' })}
          disabled={classifyMutation.isPending}>

          Classify as Rock 🪨
        </Button>
      </div>
    </div>);

}